import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LuLoaderCircle, LuCircleAlert, LuCircleCheck } from 'react-icons/lu';
import AuthShell from '../components/AuthShell';

interface Inscricao { empresa: string; recebendo: boolean }

/**
 * /resumo-diario/parar?token=… — o link "Parar de receber" do rodapé do resumo diário (17/10/2026).
 *
 * Anônima: o e-mail costuma ser aberto no celular, sem sessão. Abrir a página NÃO desliga nada —
 * antivírus de e-mail visita os links da mensagem sozinho, então parar exige o clique no botão (POST).
 * Depois de parar, a mesma tela oferece voltar, para quem clicou sem querer.
 */
const ResumoDiarioPararPage = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [inscricao, setInscricao] = useState<Inscricao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [acabouDeMudar, setAcabouDeMudar] = useState(false);
  const jaConsultou = useRef(false);

  const base = `${apiUrl}/api/public/resumo-diario/${encodeURIComponent(token)}`;

  useEffect(() => {
    if (jaConsultou.current) return;
    jaConsultou.current = true;

    if (!token) {
      setErro('Este link não é válido.');
      return;
    }

    (async () => {
      try {
        const resposta = await fetch(base);
        const dados = await resposta.json().catch(() => ({}));
        if (!resposta.ok || dados.sucesso === false) {
          setErro(dados.mensagem || 'Este link não é mais válido.');
          return;
        }
        setInscricao(dados.dados as Inscricao);
      } catch {
        setErro('Não conseguimos falar com o servidor. Tente abrir o link de novo.');
      }
    })();
  }, [base, token]);

  const mudar = async (acao: 'parar' | 'voltar') => {
    setSalvando(true);
    try {
      const resposta = await fetch(`${base}/${acao}`, { method: 'POST' });
      const dados = await resposta.json().catch(() => ({}));
      if (!resposta.ok || dados.sucesso === false) {
        setErro(dados.mensagem || 'Não foi possível salvar agora. Tente de novo em instantes.');
        return;
      }
      setInscricao(dados.dados as Inscricao);
      setAcabouDeMudar(true);
    } catch {
      setErro('Não conseguimos falar com o servidor. Tente de novo em instantes.');
    } finally {
      setSalvando(false);
    }
  };

  if (erro) {
    return (
      <AuthShell>
        <h1 className="au-h1">Resumo diário</h1>
        <div className="au-alert is-danger" role="alert">
          <LuCircleAlert size={16} />
          <span>{erro}</span>
        </div>
        <Link to="/login" className="au-btn" style={{ textDecoration: 'none' }}>
          Ir para o Brik CRM
        </Link>
      </AuthShell>
    );
  }

  if (!inscricao) {
    return (
      <AuthShell>
        <h1 className="au-h1">Resumo diário</h1>
        <p className="au-sub">
          <LuLoaderCircle size={15} className="au-spin" style={{ verticalAlign: '-2px' }} /> Só um instante.
        </p>
      </AuthShell>
    );
  }

  if (!inscricao.recebendo) {
    return (
      <AuthShell>
        <h1 className="au-h1">{acabouDeMudar ? 'Pronto, você não recebe mais' : 'Você não recebe o resumo'}</h1>
        <p className="au-sub">
          O resumo diário da <strong>{inscricao.empresa}</strong> não vai mais para o seu e-mail.
          Os outros e-mails do Brik CRM, como convite e redefinição de senha, continuam chegando.
        </p>
        <button type="button" className={`au-btn${salvando ? ' is-loading' : ''}`} disabled={salvando}
          onClick={() => mudar('voltar')}>
          {salvando ? 'Salvando…' : 'Voltar a receber'}
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="au-h1">{acabouDeMudar ? 'Você voltou a receber' : 'Parar de receber o resumo diário?'}</h1>
      {acabouDeMudar ? (
        <div className="au-alert" role="status">
          <LuCircleCheck size={16} />
          <span>A partir do próximo dia útil, o resumo da <strong>{inscricao.empresa}</strong> volta a chegar às 8h.</span>
        </div>
      ) : (
        <>
          <p className="au-sub">
            Todo dia útil às 8h você recebe o que aconteceu na <strong>{inscricao.empresa}</strong> e o
            que precisa de você. Se parar, só este e-mail deixa de chegar.
          </p>
          <button type="button" className={`au-btn${salvando ? ' is-loading' : ''}`} disabled={salvando}
            onClick={() => mudar('parar')}>
            {salvando ? 'Salvando…' : 'Parar de receber'}
          </button>
        </>
      )}
    </AuthShell>
  );
};

export default ResumoDiarioPararPage;
